import { useState, useEffect } from "react";

import { toast } from "react-toastify";

import { JourneyContentRow } from "../../../../types/games";
import { TRIVIA } from "../../../../constants";

import { Check, X } from "lucide-react";

/**
 * Props for the Trivia form component
 */
interface JourneyTriviaFormProps {
  contentRows: JourneyContentRow[];
  setContentRows: React.Dispatch<React.SetStateAction<JourneyContentRow[]>>;
  isLoading: boolean;
  touchpointId: string; // ID of the touchpoint this form is associated with
}

/**
 * A component for creating and editing Trivia questions in journeys.
 *
 * This component doesn't maintain its own state for rows - it only renders rows from
 * the parent component's state and calls parent methods to update that state.
 */
export const JourneyTriviaCreateForm = ({
  contentRows,
  setContentRows,
  isLoading,
  touchpointId,
}: JourneyTriviaFormProps) => {
  // Local state to track inputs without triggering parent updates on every keystroke
  const [localInputValues, setLocalInputValues] = useState<
    Record<number, Record<string, string>>
  >({});

  // Simplified version for checking completed rows in parent state
  const isRowComplete = (row: JourneyContentRow): boolean => {
    return Boolean(row.question && row.answer && row.option1);
  };

  // Get all rows for this touchpoint and game type
  const allTouchpointRows = contentRows.filter(
    (row) => row.touchpoint_id === touchpointId,
  );

  // Get only the incomplete rows for editing
  const relevantRows = allTouchpointRows.filter((row) => !isRowComplete(row));

  // Filter rows for preview - only show rows with complete data
  const previewRows = allTouchpointRows.filter((row) => isRowComplete(row));

  // Add an empty row if there are no relevant rows
  useEffect(() => {
    if (relevantRows.length === 0) {
      setContentRows((prevRows) => [
        ...prevRows,
        {
          id: Date.now(),
          touchpoint_id: touchpointId,
          game_type: TRIVIA,
          question: "",
          answer: "",
          option1: "",
          option2: "",
          option3: "",
        },
      ]);
    }
  }, [relevantRows.length, touchpointId, setContentRows]);

  // Add a new row with proper touchpoint and game identifiers
  const addEmptyRow = () => {
    const newRow = {
      id: Date.now(),
      touchpoint_id: touchpointId,
      game_type: TRIVIA,
    };

    // Update parent state directly
    setContentRows((current: JourneyContentRow[]) => [...current, newRow]);
  };

  // Handle input change for local state only (prevents losing focus)
  const handleInputChange = (id: number, field: string, value: string) => {
    setLocalInputValues((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  // Get the current value from local state or fallback to parent state
  const getInputValue = (row: JourneyContentRow, field: string): string => {
    if (
      localInputValues[row.id] &&
      localInputValues[row.id][field] !== undefined
    ) {
      return localInputValues[row.id][field];
    }
    return row[field] || "";
  };

  // Check if a row has all required fields filled in the local state
  const isRowValid = (id: number): boolean => {
    const row = contentRows.find((r) => r.id === id);
    if (!row) return false;

    // Get values from local state or parent state
    const question = getInputValue(row, "question");
    const answer = getInputValue(row, "answer");
    const option1 = getInputValue(row, "option1");

    // At minimum we need question, answer, and at least one option
    return Boolean(question && answer && option1);
  };

  // Handle input updates only in local state
  const handleLocalUpdate = (id: number, field: string, value: string) => {
    // Just update the local state, parent state will be updated when saved
    handleInputChange(id, field, value);
  };

  // Remove a row directly from parent state (used in JSX below)
  const removeRow = (idToRemove: number) => {
    setContentRows((prevRows) => {
      // Filter out the row to remove
      const filteredRows = prevRows.filter(
        (row: JourneyContentRow) => row.id !== idToRemove,
      );

      // Get the remaining rows for this touchpoint/game
      const remainingForTouchpoint = filteredRows.filter(
        (row: JourneyContentRow) => row.touchpoint_id === touchpointId,
      );

      // If we removed the last row for this touchpoint, add a new empty one
      if (remainingForTouchpoint.length === 0) {
        return [
          ...filteredRows,
          {
            id: Date.now(),
            touchpoint_id: touchpointId,
            game_type: TRIVIA,
          },
        ];
      }

      return filteredRows;
    });
  };

  // Save all valid rows from local state to parent state, replacing existing ones for this TP/Game
  const saveRowsToPreview = (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Get newly valid rows from the ones being edited
    const newlyValidRows = relevantRows
      .map((row) => {
        if (isRowValid(row.id)) {
          // Construct the full row data from local state
          return {
            ...row, // Keep existing fields like id, touchpoint_id, game_type
            type: "trivia", // Ensure API compatibility type
            question: getInputValue(row, "question"),
            answer: getInputValue(row, "answer"),
            option1: getInputValue(row, "option1"),
            option2: getInputValue(row, "option2"),
            option3: getInputValue(row, "option3"),
          } as JourneyContentRow; // Cast to ContentRow type
        }
        return null; // Return null for invalid rows
      })
      .filter((row): row is JourneyContentRow => row !== null); // Filter out the nulls and assert type

    // Get already completed rows
    const completedRows = allTouchpointRows.filter((row) => isRowComplete(row));

    // Combine completed and newly valid rows
    const allValidRows = [...completedRows, ...newlyValidRows];

    if (newlyValidRows.length === 0) {
      toast.error(
        "Please complete at least one valid trivia question before saving.",
      );
      return;
    }

    // 2. Update parent state: save valid rows and add a new empty one
    setContentRows((prevRows) => {
      // Filter out all existing rows for this specific touchpoint and game
      const otherRows = prevRows.filter(
        (row) => !(row.touchpoint_id === touchpointId),
      );

      // Add all valid rows (both completed and newly valid) and a new empty row
      return [
        ...otherRows,
        ...allValidRows,
        {
          id: Date.now(),
          touchpoint_id: touchpointId,
          game_type: TRIVIA,
          question: "",
          answer: "",
          option1: "",
          option2: "",
          option3: "",
        },
      ];
    });

    // 3. Clear local state for the saved rows
    const savedRowIds = new Set(newlyValidRows.map((row) => row.id));
    setLocalInputValues((prev) => {
      const newState = { ...prev };
      savedRowIds.forEach((id) => {
        delete newState[id];
      });
      return newState;
    });

    toast.success(
      `${newlyValidRows.length} question${
        newlyValidRows.length !== 1 ? "s" : ""
      } saved successfully!`,
    );
  };

  // Removed duplicate isRowValid function - using the one below

  return (
    <div className="space-y-4 relative">
      {/* Preview of saved items - only show when there are valid saved rows */}
      {previewRows.length > 0 && (
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Saved Questions
            </h4>
            <span className="text-xs text-gray-500">
              {previewRows.length} total
            </span>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {previewRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between bg-white p-2 rounded-md text-sm border border-gray-100"
              >
                <div className="flex-1 truncate">
                  <span className="font-medium">{row.question}</span>
                  <span className="mx-2 text-brand font-medium">→</span>
                  <span className="font-medium">{row.answer}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form with multiple editable rows */}
      <form onSubmit={saveRowsToPreview} className="pt-4">
        {/* Add Row button */}
        <div className="flex justify-end items-center mb-4 gap-x-4">
          <button
            onClick={saveRowsToPreview}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
          >
            <Check className="w-4 h-4 inline-block pr-2" />
            {isLoading ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            onClick={addEmptyRow}
            className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
          >
            + Add Row
          </button>
        </div>

        {/* Multiple editable rows */}
        <div className="space-y-3 mb-4">
          {relevantRows.map((row: JourneyContentRow) => (
            <div
              key={row.id}
              className="flex flex-col p-3 rounded-md bg-white shadow-sm border border-gray-200"
            >
              {/* Question and Answer section */}
              <div className="flex-grow flex flex-col space-y-3">
                <div className="flex flex-grow gap-x-4">
                  {/* Question */}
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                      placeholder="What is the capital of France?"
                      value={getInputValue(row, "question")}
                      onChange={(e) =>
                        handleLocalUpdate(row.id, "question", e.target.value)
                      }
                    />
                  </div>

                  {/* Answer */}
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">
                      Answer
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                      placeholder="Paris"
                      value={getInputValue(row, "answer")}
                      onChange={(e) =>
                        handleLocalUpdate(row.id, "answer", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Options */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Options
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3].map((num) => (
                      <input
                        key={num}
                        type="text"
                        placeholder={`Option ${num}`}
                        className="p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand w-full"
                        value={getInputValue(row, `option${num}`)}
                        onChange={(e) =>
                          handleLocalUpdate(
                            row.id,
                            `option${num}`,
                            e.target.value,
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end items-center border-t border-gray-100">
                <div className="mt-4">
                  <button
                    type="button"
                    disabled={isLoading || relevantRows.length === 1}
                    onClick={() => removeRow(row.id)}
                    className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </form>
      <div>
        <span className="text-xs font-medium text-gray-700">
          {previewRows.length} questions ready
        </span>
      </div>
    </div>
  );
};
